import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Zap, Calendar, Clock, User, MapPin, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

// Initialize Stripe - handle missing key gracefully
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

// Payment Form Component
const PaymentForm = ({ deal, onSuccess, prefillData }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: prefillData?.name || '',
    email: prefillData?.email || '',
    phone: prefillData?.phone || ''
  });

  // Update form if prefill data changes
  useEffect(() => {
    if (prefillData) {
      setFormData(prev => ({
        name: prefillData.name || prev.name,
        email: prefillData.email || prev.email,
        phone: prefillData.phone || prev.phone
      }));
    }
  }, [prefillData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) return;
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);

    try {
      // Create payment intent
      const intentResponse = await axios.post(`${API}/deals/${deal.token}/create-payment-intent`, {
        name: formData.name,
        email: formData.email
      });

      const { client_secret, payment_intent_id } = intentResponse.data;

      // Confirm payment
      const { error, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: formData.name,
            email: formData.email,
            phone: formData.phone
          }
        }
      });

      if (error) {
        toast.error(error.message);
        setLoading(false);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        // Book the deal
        const bookingResponse = await axios.post(`${API}/deals/${deal.token}/book`, {
          customer_name: formData.name,
          customer_email: formData.email,
          customer_phone: formData.phone,
          payment_intent_id: payment_intent_id
        });

        toast.success('Booking confirmed!');
        onSuccess(bookingResponse.data);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const cardStyle = {
    style: {
      base: {
        color: '#fff',
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        '::placeholder': {
          color: '#6b7280'
        }
      },
      invalid: {
        color: '#ef4444'
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Show welcome message if user is recognized */}
      {prefillData?.email && (
        <div className="bg-green-900/30 border border-green-500/30 rounded-lg p-3 mb-4">
          <p className="text-green-400 text-sm flex items-center gap-2">
            <CheckCircle size={16} />
            Welcome back! We've filled in your info.
          </p>
        </div>
      )}
      
      <div>
        <label className="text-sm text-gray-400 mb-1 block">Full Name</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Your name"
          className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-green-500 outline-none"
          required
        />
      </div>

      <div>
        <label className="text-sm text-gray-400 mb-1 block">Email</label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
          placeholder="your@email.com"
          className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:border-green-500 outline-none ${prefillData?.email ? 'border-green-500/50' : 'border-gray-700'}`}
          required
        />
      </div>

      <div>
        <label className="text-sm text-gray-400 mb-1 block">Phone</label>
        <input
          type="tel"
          value={formData.phone}
          onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
          placeholder="(555) 555-5555"
          className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-green-500 outline-none"
          required
        />
      </div>

      <div>
        <label className="text-sm text-gray-400 mb-1 block">Card Details</label>
        <div className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg">
          <CardElement options={cardStyle} />
        </div>
      </div>

      <button
        type="submit"
        disabled={!stripe || loading}
        className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-bold text-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            Processing...
          </>
        ) : (
          <>
            <CheckCircle size={20} />
            Pay ${deal.deal_price} & Book Now
          </>
        )}
      </button>

      <p className="text-center text-gray-500 text-xs">
        Secure payment powered by Stripe
      </p>
    </form>
  );
};

// Main Deal Page Component
const DealBooking = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [prefillData, setPrefillData] = useState(null);

  // Parse user info from URL parameter
  useEffect(() => {
    const encodedUserInfo = searchParams.get('u');
    if (encodedUserInfo) {
      try {
        // Decode base64 user info
        const decoded = atob(encodedUserInfo);
        const [email, name, phone] = decoded.split('|');
        setPrefillData({
          email: email || '',
          name: name || '',
          phone: phone || ''
        });
      } catch (e) {
        console.error('Error decoding user info:', e);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchDealData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/deals/${token}`);
        setDeal(response.data);
      } catch (err) {
        console.error('Error fetching deal:', err);
        setError('Deal not found or has expired.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDealData();
  }, [token]);

  const handleSuccess = (data) => {
    setBookingDetails(data);
    setBookingConfirmed(true);
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'long', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <img src="/logo.png" alt="HQ Studios" className="h-16 w-16 rounded-full mx-auto mb-4 border-2 border-purple-500/50" />
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading your exclusive deal...</p>
        </div>
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-purple-900/40 to-blue-900/40 border border-purple-500/30 rounded-2xl p-8 max-w-md text-center">
          <img src="/logo.png" alt="HQ Studios" className="h-16 w-16 rounded-full mx-auto mb-4 border-2 border-purple-500/50" />
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Deal Not Found</h2>
          <p className="text-gray-400">{error || 'This deal may have expired or been claimed.'}</p>
          <a 
            href="/" 
            className="inline-block mt-6 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg font-semibold transition-all"
          >
            Book a Regular Session
          </a>
        </div>
      </div>
    );
  }

  if (deal.is_expired) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-purple-900/40 to-blue-900/40 border border-purple-500/30 rounded-2xl p-8 max-w-md text-center">
          <img src="/logo.png" alt="HQ Studios" className="h-16 w-16 rounded-full mx-auto mb-4 border-2 border-purple-500/50" />
          <AlertTriangle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Deal Expired</h2>
          <p className="text-gray-400">Sorry, this deal has expired. Check back for future exclusive deals!</p>
          <a 
            href="/" 
            className="inline-block mt-6 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg font-semibold transition-all"
          >
            Book a Regular Session
          </a>
        </div>
      </div>
    );
  }

  if (deal.is_sold_out) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-purple-900/40 to-blue-900/40 border border-purple-500/30 rounded-2xl p-8 max-w-md text-center">
          <img src="/logo.png" alt="HQ Studios" className="h-16 w-16 rounded-full mx-auto mb-4 border-2 border-purple-500/50" />
          <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Sold Out!</h2>
          <p className="text-gray-400">This deal has been claimed. Follow us on Instagram for more deals!</p>
          <a 
            href="/" 
            className="inline-block mt-6 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg font-semibold transition-all"
          >
            Book a Regular Session
          </a>
        </div>
      </div>
    );
  }

  if (bookingConfirmed) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-purple-900/40 to-blue-900/40 border border-green-500/30 rounded-2xl p-8 max-w-md text-center">
          <img src="/logo.png" alt="HQ Studios" className="h-16 w-16 rounded-full mx-auto mb-4 border-2 border-green-500/50" />
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">You're Booked! 🎉</h2>
          <p className="text-gray-400 mb-6">
            Check your email for confirmation and session details.
          </p>
          
          <div className="bg-black/40 rounded-xl p-6 mb-6 text-left border border-purple-500/20">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Booking #:</span>
                <span className="text-white font-semibold">{bookingDetails?.booking_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Date:</span>
                <span className="text-white font-semibold">{formatDate(deal.date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Time:</span>
                <span className="text-white font-semibold">{deal.time}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Engineer:</span>
                <span className="text-white font-semibold">{deal.engineer_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Amount Paid:</span>
                <span className="text-green-400 font-bold">${deal.deal_price}</span>
              </div>
            </div>
          </div>
          
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-center gap-2 text-purple-400 mb-2">
              <MapPin className="w-4 h-4" />
              <span className="font-semibold text-sm">Studio Location</span>
            </div>
            <p className="text-gray-300 text-sm text-center">
              High Quality Studios<br />
              270 Meserole Street, Brooklyn, NY 11206
            </p>
          </div>

          <a 
            href="/" 
            className="block w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg font-semibold transition-all text-center"
          >
            Back to Website
          </a>
        </div>
      </div>
    );
  }

  const savings = deal.original_price - deal.deal_price;
  const savingsPercent = Math.round((savings / deal.original_price) * 100);

  return (
    <div className="min-h-screen bg-black">
      {/* Header with Logo */}
      <header className="bg-black/80 backdrop-blur-lg border-b border-purple-900/30 sticky top-0 z-50" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center justify-center">
            <a href="/" className="flex items-center gap-3">
              <img 
                src="/logo.png" 
                alt="HQ Studios" 
                className="h-10 w-10 rounded-full object-cover border-2 border-purple-500/50"
              />
              <span className="text-white font-bold text-xl">HQ Studios</span>
            </a>
          </div>
        </div>
      </header>

      <div className="py-8 px-4">
        <div className="max-w-lg mx-auto">
          {/* Exclusive Deal Badge */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4 animate-pulse">
              <Zap size={16} />
              EXCLUSIVE DEAL • {deal.slots_remaining} SPOT{deal.slots_remaining > 1 ? 'S' : ''} LEFT!
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">{deal.title}</h1>
            {deal.description && (
              <p className="text-gray-400">{deal.description}</p>
            )}
          </div>

          {/* Deal Card - Matching Site Style */}
          <div className="bg-gradient-to-br from-purple-900/40 to-blue-900/40 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-6 mb-6 shadow-2xl">
            {/* Price Display */}
            <div className="text-center mb-6 py-4 bg-black/30 rounded-xl">
              <p className="text-gray-400 text-sm mb-2">Special Price</p>
              <div className="flex items-center justify-center gap-3">
                <span className="text-gray-500 text-2xl line-through">${deal.original_price}</span>
                <span className="text-green-400 text-5xl font-bold">${deal.deal_price}</span>
              </div>
              <div className="mt-2">
                <span className="bg-green-500/20 text-green-400 px-4 py-1 rounded-full text-sm font-bold border border-green-500/30">
                  🔥 SAVE ${savings} ({savingsPercent}% OFF)
                </span>
              </div>
            </div>

            {/* Session Details */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="bg-black/40 rounded-xl p-4 text-center border border-purple-500/20">
                <User className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                <p className="text-xs text-gray-500 uppercase tracking-wide">Engineer</p>
                <p className="text-white font-semibold">{deal.engineer_name}</p>
              </div>
              <div className="bg-black/40 rounded-xl p-4 text-center border border-purple-500/20">
                <Clock className="w-6 h-6 text-orange-400 mx-auto mb-2" />
                <p className="text-xs text-gray-500 uppercase tracking-wide">Duration</p>
                <p className="text-white font-semibold">{deal.duration_hours} Hours</p>
              </div>
              <div className="bg-black/40 rounded-xl p-4 text-center col-span-2 border border-purple-500/20">
                <Calendar className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                <p className="text-xs text-gray-500 uppercase tracking-wide">Session Date & Time</p>
                <p className="text-white font-semibold">{formatDate(deal.date)} at {deal.time}</p>
              </div>
            </div>

            {/* Location with Map Link */}
            <a 
              href="https://maps.google.com/?q=270+Meserole+St+Brooklyn+NY" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block bg-black/40 rounded-xl p-4 text-center border border-purple-500/20 hover:border-purple-500/50 transition-colors"
            >
              <MapPin className="w-5 h-5 text-green-400 mx-auto mb-1" />
              <p className="text-gray-300 text-sm">
                High Quality Studios • 270 Meserole St, Brooklyn NY
              </p>
              <p className="text-purple-400 text-xs mt-1">📍 Tap for directions</p>
            </a>
          </div>

          {/* Payment Form - Matching Site Style */}
          <div className="bg-gradient-to-br from-purple-900/40 to-blue-900/40 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-6 shadow-2xl">
            <h2 className="text-xl font-bold text-white mb-2 text-center">
              Complete Your Booking
            </h2>
            <p className="text-gray-400 text-sm text-center mb-6">
              Secure checkout • Full payment required
            </p>
            
            <Elements stripe={stripePromise}>
              <PaymentForm deal={deal} onSuccess={handleSuccess} prefillData={prefillData} />
            </Elements>
          </div>

          {/* Trust Badges */}
          <div className="mt-6 grid grid-cols-3 gap-2 text-center">
            <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-800">
              <span className="text-2xl">🔒</span>
              <p className="text-gray-400 text-xs mt-1">Secure Payment</p>
            </div>
            <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-800">
              <span className="text-2xl">✅</span>
              <p className="text-gray-400 text-xs mt-1">Verified Studio</p>
            </div>
            <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-800">
              <span className="text-2xl">⭐</span>
              <p className="text-gray-400 text-xs mt-1">5-Star Rated</p>
            </div>
          </div>

          {/* Urgency Footer */}
          <div className="mt-6 text-center">
            <p className="text-red-400 text-sm">
              ⏰ Deal expires 2 hours before session time
            </p>
            <p className="text-gray-500 text-xs mt-2">
              Questions? Call us: (718) 705-4203
            </p>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-800 text-center">
            <p className="text-gray-500 text-xs">
              © {new Date().getFullYear()} High Quality Studios • Brooklyn, NY
            </p>
            <div className="flex justify-center gap-4 mt-2">
              <a href="/" className="text-purple-400 text-xs hover:text-purple-300">Website</a>
              <a href="https://instagram.com/hqstudios" target="_blank" rel="noopener noreferrer" className="text-purple-400 text-xs hover:text-purple-300">Instagram</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealBooking;
